const BASE = "";

const defaultFetch = (url: string, init?: RequestInit) =>
  fetch(url, { ...init, credentials: "include" });

async function handleRes<T>(res: Response): Promise<T> {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = data as { error?: string; message?: string; code?: string };
    throw new Error(err.message ?? err.error ?? res.statusText);
  }
  return data as T;
}

export interface Company {
  id: string;
  name: string;
  planId: string;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  subscriptionStatus: string | null;
  notificationSubjectPrefix?: string | null;
  notificationBodyTemplate?: string | null;
  reportFormatSystemPrompt?: string | null;
  reportFormatUserPrefix?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface User {
  id: string;
  companyId: string;
  email: string;
  name: string | null;
  role: string;
  calendarWatchChannelId: string | null;
  calendarWatchExpiration: string | null;
  calendarWatchResourceId: string | null;
  googleLinked?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Plan {
  id: string;
  name: string;
  stripePriceId: string | null;
  maxUsers: number | null;
}

export interface Deal {
  id: string;
  userId: string | null;
  companyId: string;
  googleEventId: string;
  title: string;
  startAt: string;
  endAt: string;
  rawReport: string | null;
  fixedReport: string | null;
  notificationMailMessageId: string | null;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MeResponse {
  user: User;
  company: Company;
  plan: Plan;
}

export interface SubscriptionStatus {
  planId: string;
  status: string | null;
  quantity: number | null;
  currentPeriodEnd: number | null;
}

export interface HearingItem {
  id: string;
  label: string;
  sortOrder: number;
}

export const api = {
  auth: {
    me: () => defaultFetch(`${BASE}/api/auth/me`).then(handleRes<MeResponse>),
    login: (body: { email: string; password: string }) =>
      defaultFetch(`${BASE}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }).then(handleRes<{ user: User; company: Company }>),
    signup: (body: { companyName: string; email: string; password: string; name?: string }) =>
      defaultFetch(`${BASE}/api/auth/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }).then(handleRes<{ user: User; company: Company }>),
    logout: () =>
      defaultFetch(`${BASE}/api/auth/logout`, { method: "POST" }).then(handleRes<{ ok: boolean }>),
    setPassword: (body: { inviteToken?: string; token?: string; password: string }) =>
      defaultFetch(`${BASE}/api/auth/set-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: body.token ?? body.inviteToken, password: body.password }),
      }).then(handleRes<{ ok: boolean }>),
    requestPasswordReset: (body: { email: string }) =>
      defaultFetch(`${BASE}/api/auth/request-password-reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }).then(handleRes<{ ok: boolean }>),
  },
  billing: {
    createCheckoutSession: (body?: { successUrl?: string; cancelUrl?: string }) =>
      defaultFetch(`${BASE}/api/billing/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body ?? {}),
      }).then(handleRes<{ url: string }>),
    createPortalSession: (body?: { returnUrl?: string }) =>
      defaultFetch(`${BASE}/api/billing/portal`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body ?? {}),
      }).then(handleRes<{ url: string }>),
    getSubscription: () =>
      defaultFetch(`${BASE}/api/billing/subscription`).then(handleRes<SubscriptionStatus>),
  },
  companies: {
    list: () => defaultFetch(`${BASE}/api/companies`).then(handleRes<Company[]>),
    getHearingItems: (companyId: string) =>
      defaultFetch(`${BASE}/api/companies/${encodeURIComponent(companyId)}/hearing-items`).then(
        handleRes<{ items: HearingItem[] }>
      ),
    updateHearingItems: (companyId: string, items: { label: string; sortOrder: number }[]) =>
      defaultFetch(`${BASE}/api/companies/${encodeURIComponent(companyId)}/hearing-items`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      }).then(handleRes<{ items: HearingItem[] }>),
    updatePromptSettings: (companyId: string, body: {
      notificationSubjectPrefix?: string | null;
      notificationBodyTemplate?: string | null;
      reportFormatSystemPrompt?: string | null;
      reportFormatUserPrefix?: string | null;
    }) =>
      defaultFetch(`${BASE}/api/companies/${encodeURIComponent(companyId)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }).then(handleRes<Company>),
  },
  users: {
    listByCompany: (companyId: string) =>
      defaultFetch(`${BASE}/api/companies/${companyId}/users`).then(handleRes<User[]>),
    create: (body: { email: string; name?: string }) =>
      defaultFetch(`${BASE}/api/users`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }).then(handleRes<User>),
    removeFromMonitoring: (userId: string) =>
      defaultFetch(`${BASE}/api/users/${userId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ monitored: false }),
      }).then(handleRes<{ ok: boolean }>),
    delete: (userId: string) =>
      defaultFetch(`${BASE}/api/users/${userId}`, { method: "DELETE" }).then(async res => {
        if (!res.ok) return handleRes<never>(res);
      }),
    getInviteLink: (userId: string) =>
      defaultFetch(`${BASE}/api/users/${userId}/invite-link`).then(
        handleRes<{ inviteLink: string; expiresAt: string }>
      ),
  },
  deals: {
    list: (params?: { companyId?: string; userId?: string }) => {
      const sp = new URLSearchParams();
      if (params?.companyId) sp.set("companyId", params.companyId);
      if (params?.userId) sp.set("userId", params.userId);
      return defaultFetch(`${BASE}/api/deals?${sp}`).then(handleRes<Deal[]>);
    },
    get: (id: string) => defaultFetch(`${BASE}/api/deals/${id}`).then(handleRes<Deal>),
  },
};
